import React, { useState, useEffect } from 'react';
import { useBrand } from '../../context/BrandContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Tooltip,
  Paper,
  Grid,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Warning as ExpiredIcon,
  History as HistoryIcon,
  DeleteSweep as BulkDeleteIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import {
  getDisposals,
  createDisposal,
  updateDisposal,
  deleteDisposal,
  getDisposalSummary,
  getExpiredBatchesForDisposal,
  bulkDisposeExpired,
  bulkCreateDisposals,
  getWarehouses
} from '../../services/inventoryService';

const DISPOSAL_TYPES = [
  { value: 'expired', label: 'Expired' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'write_off', label: 'Write Off' },
  { value: 'sample', label: 'Sample/Gift' },
  { value: 'theft', label: 'Theft/Loss' },
  { value: 'other', label: 'Other' }
];

function Disposals() {
  const { selectedBrand } = useBrand();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Data
  const [disposals, setDisposals] = useState([]);
  const [expiredBatches, setExpiredBatches] = useState([]);
  const [summary, setSummary] = useState(null);
  const [warehouses, setWarehouses] = useState([]);

  // Dialog state
  const [disposalDialog, setDisposalDialog] = useState({ open: false, data: null, editId: null });
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, disposal: null });
  const [bulkConfirmDialog, setBulkConfirmDialog] = useState({ open: false, selected: [] });
  // Bulk CSV upload dialog: parsed rows + client-side validation + submit results.
  const [bulkUploadDialog, setBulkUploadDialog] = useState({ open: false });
  const [bulkRows, setBulkRows] = useState([]);        // [{ _row, warehouse_code, sku, ... , _error }]
  const [bulkFileName, setBulkFileName] = useState('');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);  // { created, failed, results } after submit

  // Filters
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [selectedType, setSelectedType] = useState('');

  // Selection for expired batches
  const [selectedBatches, setSelectedBatches] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [disposalsRes, expiredRes, summaryRes, warehousesRes] = await Promise.all([
        getDisposals({ limit: 100 }),
        getExpiredBatchesForDisposal(),
        getDisposalSummary(),
        getWarehouses({ is_active: true })
      ]);
      setDisposals(disposalsRes?.data || []);
      setExpiredBatches(expiredRes?.data || []);
      setSummary(summaryRes?.data || null);
      setWarehouses(warehousesRes?.data || warehousesRes?.warehouses || []);
    } catch (err) {
      console.error('Failed to load disposals data:', err);
      setError('Failed to load disposal data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDisposal = async () => {
    try {
      const { data, editId } = disposalDialog;
      if (editId) {
        await updateDisposal(editId, data);
        setSuccess('Disposal updated successfully.');
      } else {
        await createDisposal({ ...data, brand_id: selectedBrand?.id || 0 });
        setSuccess('Disposal recorded successfully. Inventory has been deducted using FIFO.');
      }
      setDisposalDialog({ open: false, data: null, editId: null });
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to save disposal');
    }
  };

  const handleEditDisposal = (disposal) => {
    setError(null); setDisposalDialog({
      open: true,
      editId: disposal.id,
      data: {
        sku: disposal.sku,
        warehouse_code: disposal.warehouse_code,
        disposal_type: disposal.disposal_type,
        quantity: parseFloat(disposal.quantity) || '',
        disposal_date: disposal.disposal_date ? String(disposal.disposal_date).split('T')[0] : '',
        unit_cost: parseFloat(disposal.unit_cost) || '',
        batch_number: disposal.batch_number || '',
        reference_number: disposal.reference_number || '',
        reason: disposal.reason || '',
        notes: disposal.notes || ''
      }
    });
  };

  const handleDeleteDisposal = async () => {
    try {
      const { disposal } = deleteConfirmDialog;
      await deleteDisposal(disposal.id);
      setSuccess(`Deleted disposal of ${disposal.quantity} ${disposal.sku} at ${disposal.warehouse_code}. Stock has been restored.`);
      setDeleteConfirmDialog({ open: false, disposal: null });
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to delete disposal');
    }
  };

  const handleBulkDisposeExpired = async () => {
    try {
      const batchIds = selectedBatches.map(b => b.id);
      await bulkDisposeExpired({ batch_ids: batchIds, notes: 'Bulk expired disposal' });
      setSuccess(`Successfully disposed ${batchIds.length} expired batches`);
      setBulkConfirmDialog({ open: false, selected: [] });
      setSelectedBatches([]);
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to bulk dispose expired batches');
    }
  };

  const getWarehouseName = (codeOrId) => {
    const wh = warehouses.find(w => w.code === codeOrId || w.id === codeOrId);
    return wh ? `${wh.name} (${wh.code})` : codeOrId || '-';
  };

  const toggleBatchSelection = (batch) => {
    setSelectedBatches(prev => {
      const exists = prev.find(b => b.id === batch.id);
      if (exists) {
        return prev.filter(b => b.id !== batch.id);
      } else {
        return [...prev, batch];
      }
    });
  };

  const toggleAllBatches = () => {
    if (selectedBatches.length === expiredBatches.length) {
      setSelectedBatches([]);
    } else {
      setSelectedBatches([...expiredBatches]);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatCurrency = (val) => {
    if (!val) return '-';
    return `$${parseFloat(val).toFixed(2)}`;
  };

  // --- Bulk CSV upload ---------------------------------------------------
  // Minimal quote-aware CSV parser (handles commas inside "quoted" reasons and
  // "" escaped quotes). Header is lower-cased + spaces→underscores so
  // "Warehouse Code" and "warehouse_code" both map to warehouse_code.
  const parseCSV = (text) => {
    const lines = String(text).replace(/\r\n?/g, '\n').split('\n').filter((l) => l.trim() !== '');
    if (lines.length === 0) return { headers: [], rows: [] };
    const splitLine = (line) => {
      const out = []; let cur = ''; let inQ = false;
      for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (inQ) {
          if (c === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else { inQ = false; } }
          else { cur += c; }
        } else if (c === '"') { inQ = true; }
        else if (c === ',') { out.push(cur); cur = ''; }
        else { cur += c; }
      }
      out.push(cur);
      return out.map((s) => s.trim());
    };
    const headers = splitLine(lines[0]).map((h) => h.toLowerCase().replace(/\s+/g, '_'));
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const vals = splitLine(lines[i]);
      const obj = {};
      headers.forEach((h, idx) => { obj[h] = vals[idx] !== undefined ? vals[idx] : ''; });
      rows.push(obj);
    }
    return { headers, rows };
  };

  const REQUIRED_DISPOSAL_COLS = ['warehouse_code', 'sku', 'quantity', 'disposal_type', 'disposal_date'];

  const handleBulkFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setBulkFileName(file.name);
    setBulkResult(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const { rows } = parseCSV(ev.target.result || '');
        const validTypes = DISPOSAL_TYPES.map((t) => t.value);
        const parsed = rows.map((r, idx) => {
          const errs = [];
          REQUIRED_DISPOSAL_COLS.forEach((f) => { if (!String(r[f] || '').trim()) errs.push(`${f} missing`); });
          const q = Number(r.quantity);
          if (String(r.quantity || '').trim() && !(q > 0)) errs.push('quantity must be a number > 0');
          const dt = String(r.disposal_type || '').trim();
          if (dt && !validTypes.includes(dt)) errs.push(`disposal_type must be one of: ${validTypes.join(', ')}`);
          return { _row: idx + 1, ...r, _error: errs.join('; ') };
        });
        setBulkRows(parsed);
        if (parsed.length === 0) setError('CSV had no data rows.');
      } catch (err) {
        setError('Could not parse CSV: ' + err.message);
        setBulkRows([]);
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // allow re-selecting the same file
  };

  const submitBulkUpload = async () => {
    const valid = bulkRows.filter((r) => !r._error);
    if (valid.length === 0) { setError('No valid rows to upload — fix the flagged rows first.'); return; }
    setBulkSubmitting(true);
    setError(null);
    try {
      const payload = valid.map((r) => ({
        warehouse_code: r.warehouse_code,
        sku: r.sku,
        quantity: Number(r.quantity),
        disposal_type: r.disposal_type,
        disposal_date: r.disposal_date,
        unit_cost: Number(r.unit_cost) || 0,
        reason: r.reason || '',
        batch_number: r.batch_number || '',
        reference_number: r.reference_number || '',
        notes: r.notes || '',
        brand_id: String(r.brand_id || '').trim() ? parseInt(r.brand_id, 10) : (selectedBrand?.id || 0),
      }));
      const res = await bulkCreateDisposals(payload);
      setBulkResult(res);
      if (res && res.created > 0) {
        setSuccess(`Recorded ${res.created} disposal(s)${res.failed ? `, ${res.failed} failed` : ''}.`);
        loadData();
      } else if (res) {
        setError(`No disposals recorded — ${res.failed || 0} row(s) failed.`);
      }
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.error) || err.message || 'Bulk upload failed');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const downloadDisposalTemplate = () => {
    const header = 'warehouse_code,sku,quantity,disposal_type,disposal_date,unit_cost,reason,batch_number,reference_number,notes,brand_id';
    const example = 'CFP,SS0001432035,299,sample,2026-03-20,2.35,Cosmoprof exhibition samples,,,,10';
    const blob = new Blob([header + '\n' + example + '\n'], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'disposal_bulk_template.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const closeBulkUpload = () => {
    setBulkUploadDialog({ open: false });
    setBulkRows([]); setBulkFileName(''); setBulkResult(null); setBulkSubmitting(false);
  };

  const bulkValidCount = bulkRows.filter((r) => !r._error).length;
  const bulkErrorCount = bulkRows.length - bulkValidCount;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Inventory Disposals
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadData}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => { setBulkResult(null); setBulkRows([]); setBulkFileName(''); setError(null); setBulkUploadDialog({ open: true }); }}
          >
            Bulk Upload
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setError(null); setDisposalDialog({
              open: true,
              editId: null,
              data: {
                sku: '',
                warehouse_code: '',
                disposal_type: 'expired',
                quantity: '',
                disposal_date: new Date().toISOString().split('T')[0],
                unit_cost: '',
                batch_number: '',
                reason: '',
                notes: ''
              }
            }); }}
          >
            Record Disposal
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Total Disposals (30d)</Typography>
                <Typography variant="h4">{summary.total_disposals || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Units Disposed</Typography>
                <Typography variant="h4">{summary.total_quantity || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Value Lost</Typography>
                <Typography variant="h4" color="error">{formatCurrency(summary.total_value)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Expired Batches Pending</Typography>
                <Typography variant="h4" color="warning.main">{expiredBatches.length}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab icon={<HistoryIcon />} label="Disposal History" iconPosition="start" />
          <Tab
            icon={<ExpiredIcon />}
            label={`Expired Batches (${expiredBatches.length})`}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab 0: Disposal History */}
      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Recent Disposals</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Warehouse</InputLabel>
                  <Select
                    value={selectedWarehouse}
                    label="Warehouse"
                    onChange={(e) => setSelectedWarehouse(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {warehouses.map(w => <MenuItem key={w.id} value={w.code}>{w.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={selectedType}
                    label="Type"
                    onChange={(e) => setSelectedType(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {DISPOSAL_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>Warehouse</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Value Lost</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {disposals.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">No disposals recorded yet.</TableCell>
                    </TableRow>
                  ) : (
                    disposals
                      .filter(d => !selectedWarehouse || d.warehouse_code === selectedWarehouse)
                      .filter(d => !selectedType || d.disposal_type === selectedType)
                      .map(d => (
                        <TableRow key={d.id}>
                          <TableCell>{formatDate(d.disposal_date)}</TableCell>
                          <TableCell><code>{d.sku}</code></TableCell>
                          <TableCell>{getWarehouseName(d.warehouse_code)}</TableCell>
                          <TableCell>
                            <Chip
                              label={DISPOSAL_TYPES.find(t => t.value === d.disposal_type)?.label || d.disposal_type}
                              size="small"
                              color={d.disposal_type === 'expired' ? 'warning' : 'default'}
                            />
                          </TableCell>
                          <TableCell align="right">{d.quantity}</TableCell>
                          <TableCell align="right" sx={{ color: 'error.main' }}>{formatCurrency(d.total_value)}</TableCell>
                          <TableCell>{d.reason || d.notes || '-'}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="Edit disposal">
                              <IconButton size="small" onClick={() => handleEditDisposal(d)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete disposal">
                              <IconButton size="small" color="error" onClick={() => { setError(null); setDeleteConfirmDialog({ open: true, disposal: d }); }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Tab 1: Expired Batches */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Expired Batches Pending Disposal</Typography>
              {selectedBatches.length > 0 && (
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<BulkDeleteIcon />}
                  onClick={() => { setError(null); setBulkConfirmDialog({ open: true, selected: selectedBatches }); }}
                >
                  Dispose Selected ({selectedBatches.length})
                </Button>
              )}
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              These batches have passed their expiry date and should be disposed. Select batches and click "Dispose Selected" to record disposal and deduct inventory.
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedBatches.length === expiredBatches.length && expiredBatches.length > 0}
                        indeterminate={selectedBatches.length > 0 && selectedBatches.length < expiredBatches.length}
                        onChange={toggleAllBatches}
                      />
                    </TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>Batch #</TableCell>
                    <TableCell>Warehouse</TableCell>
                    <TableCell>Expiry Date</TableCell>
                    <TableCell align="right">Qty Remaining</TableCell>
                    <TableCell align="right">Unit Cost</TableCell>
                    <TableCell align="right">Total Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expiredBatches.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Box sx={{ py: 3 }}>
                          <Typography color="success.main">No expired batches pending disposal!</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    expiredBatches.map(batch => (
                      <TableRow key={batch.id} selected={selectedBatches.some(b => b.id === batch.id)}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedBatches.some(b => b.id === batch.id)}
                            onChange={() => toggleBatchSelection(batch)}
                          />
                        </TableCell>
                        <TableCell><code>{batch.sku}</code></TableCell>
                        <TableCell>{batch.batch_number || '-'}</TableCell>
                        <TableCell>{getWarehouseName(batch.warehouse_code)}</TableCell>
                        <TableCell>
                          <Chip label={formatDate(batch.expiry_date)} color="error" size="small" />
                        </TableCell>
                        <TableCell align="right">{batch.current_quantity}</TableCell>
                        <TableCell align="right">{formatCurrency(batch.unit_cost)}</TableCell>
                        <TableCell align="right" sx={{ color: 'error.main' }}>
                          {formatCurrency(batch.current_quantity * batch.unit_cost)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Manual Disposal Dialog (Create / Edit) */}
      <Dialog open={disposalDialog.open} onClose={() => setDisposalDialog({ open: false, data: null, editId: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{disposalDialog.editId ? 'Edit Disposal' : 'Record Inventory Disposal'}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {disposalDialog.editId
              ? 'Edit disposal details. Changes will update stock levels.'
              : 'Record a manual disposal of inventory. Stock will be deducted from the oldest batches first (FIFO).'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="SKU"
              value={disposalDialog.data?.sku || ''}
              onChange={(e) => setDisposalDialog(prev => ({ ...prev, data: { ...prev.data, sku: e.target.value } }))}
              placeholder="Product SKU"
              fullWidth
              required
              disabled={!!disposalDialog.editId}
            />
            {disposalDialog.editId ? (
              <TextField
                label="Warehouse"
                value={disposalDialog.data?.warehouse_code || ''}
                fullWidth
                disabled
              />
            ) : (
              <FormControl fullWidth required>
                <InputLabel>Warehouse</InputLabel>
                <Select
                  value={disposalDialog.data?.warehouse_code || ''}
                  label="Warehouse"
                  onChange={(e) => setDisposalDialog(prev => ({ ...prev, data: { ...prev.data, warehouse_code: e.target.value } }))}
                >
                  {warehouses.map(w => <MenuItem key={w.id} value={w.code}>{w.name} ({w.code})</MenuItem>)}
                </Select>
              </FormControl>
            )}
            <FormControl fullWidth required>
              <InputLabel>Disposal Type</InputLabel>
              <Select
                value={disposalDialog.data?.disposal_type || 'expired'}
                label="Disposal Type"
                onChange={(e) => setDisposalDialog(prev => ({ ...prev, data: { ...prev.data, disposal_type: e.target.value } }))}
              >
                {DISPOSAL_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Quantity"
              type="number"
              value={disposalDialog.data?.quantity || ''}
              onChange={(e) => setDisposalDialog(prev => ({ ...prev, data: { ...prev.data, quantity: parseInt(e.target.value) || '' } }))}
              placeholder="Number of units to dispose"
              fullWidth
              required
              inputProps={{ min: 1 }}
            />
            <TextField
              label={disposalDialog.editId ? "Disposal Date (cannot change)" : "Disposal Date"}
              type="date"
              value={disposalDialog.data?.disposal_date || ''}
              onChange={(e) => setDisposalDialog(prev => ({ ...prev, data: { ...prev.data, disposal_date: e.target.value } }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
              disabled={!!disposalDialog.editId}
            />
            <TextField
              label="Unit Cost"
              type="number"
              value={disposalDialog.data?.unit_cost || ''}
              onChange={(e) => setDisposalDialog(prev => ({ ...prev, data: { ...prev.data, unit_cost: parseFloat(e.target.value) || '' } }))}
              placeholder="Cost per unit"
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              label="Batch Number"
              value={disposalDialog.data?.batch_number || ''}
              onChange={(e) => setDisposalDialog(prev => ({ ...prev, data: { ...prev.data, batch_number: e.target.value } }))}
              placeholder="Optional batch number"
              fullWidth
            />
            <TextField
              label="Reason"
              value={disposalDialog.data?.reason || ''}
              onChange={(e) => setDisposalDialog(prev => ({ ...prev, data: { ...prev.data, reason: e.target.value } }))}
              placeholder="Reason for disposal"
              fullWidth
            />
            <TextField
              label="Notes"
              value={disposalDialog.data?.notes || ''}
              onChange={(e) => setDisposalDialog(prev => ({ ...prev, data: { ...prev.data, notes: e.target.value } }))}
              placeholder="Additional notes"
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisposalDialog({ open: false, data: null, editId: null })}>Cancel</Button>
          <Button
            variant="contained"
            color={disposalDialog.editId ? 'primary' : 'warning'}
            startIcon={disposalDialog.editId ? <EditIcon /> : <DeleteIcon />}
            onClick={handleSaveDisposal}
            disabled={!disposalDialog.data?.sku || (!disposalDialog.editId && !disposalDialog.data?.warehouse_code) || !disposalDialog.data?.quantity}
          >
            {disposalDialog.editId ? 'Save Changes' : 'Record Disposal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmDialog.open} onClose={() => setDeleteConfirmDialog({ open: false, disposal: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Disposal</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will permanently delete this disposal record and restore {deleteConfirmDialog.disposal?.quantity || 0} units back to stock.
          </Alert>
          {deleteConfirmDialog.disposal && (
            <Box>
              <Typography variant="body2"><strong>SKU:</strong> {deleteConfirmDialog.disposal.sku}</Typography>
              <Typography variant="body2"><strong>Warehouse:</strong> {getWarehouseName(deleteConfirmDialog.disposal.warehouse_code)}</Typography>
              <Typography variant="body2"><strong>Quantity:</strong> {deleteConfirmDialog.disposal.quantity}</Typography>
              <Typography variant="body2"><strong>Type:</strong> {deleteConfirmDialog.disposal.disposal_type}</Typography>
              <Typography variant="body2"><strong>Date:</strong> {formatDate(deleteConfirmDialog.disposal.disposal_date)}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmDialog({ open: false, disposal: null })}>Cancel</Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={handleDeleteDisposal}>
            Delete Disposal
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Dispose Confirmation Dialog */}
      <Dialog open={bulkConfirmDialog.open} onClose={() => setBulkConfirmDialog({ open: false, selected: [] })} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Bulk Disposal</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action will permanently dispose {selectedBatches.length} expired batch(es) and deduct the inventory.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Total units to dispose:</strong> {selectedBatches.reduce((sum, b) => sum + b.current_quantity, 0)}
          </Typography>
          <Typography variant="body2" color="error">
            <strong>Total value loss:</strong> {formatCurrency(selectedBatches.reduce((sum, b) => sum + (b.current_quantity * b.unit_cost), 0))}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkConfirmDialog({ open: false, selected: [] })}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<BulkDeleteIcon />}
            onClick={handleBulkDisposeExpired}
          >
            Confirm Disposal
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk CSV upload */}
      <Dialog open={bulkUploadDialog.open} onClose={closeBulkUpload} maxWidth="md" fullWidth>
        <DialogTitle>Bulk Upload Disposals</DialogTitle>
        <DialogContent dividers>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload a CSV to record many disposals at once. Required columns:{' '}
            <b>warehouse_code, sku, quantity, disposal_type, disposal_date</b>. Optional:{' '}
            unit_cost, reason, batch_number, reference_number, notes, brand_id. disposal_type must be one of:{' '}
            {DISPOSAL_TYPES.map((t) => t.value).join(', ')}. Date format YYYY-MM-DD. Rows with errors are skipped.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadDisposalTemplate}>
              Download template
            </Button>
            <Button variant="contained" component="label" startIcon={<UploadIcon />}>
              Choose CSV
              <input type="file" accept=".csv,text/csv" hidden onChange={handleBulkFile} />
            </Button>
            {bulkFileName && <Typography variant="body2">{bulkFileName}</Typography>}
          </Box>

          {bulkRows.length > 0 && !bulkResult && (
            <>
              <Alert severity={bulkErrorCount ? 'warning' : 'success'} sx={{ mb: 1 }}>
                {bulkValidCount} valid row(s){bulkErrorCount ? `, ${bulkErrorCount} with errors (will be skipped)` : ''}.
              </Alert>
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Warehouse</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell>Qty</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Unit cost</TableCell>
                      <TableCell>Issue</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bulkRows.slice(0, 100).map((r) => (
                      <TableRow key={r._row} sx={r._error ? { bgcolor: 'error.light' } : {}}>
                        <TableCell>{r._row}</TableCell>
                        <TableCell>{r.warehouse_code}</TableCell>
                        <TableCell>{r.sku}</TableCell>
                        <TableCell>{r.quantity}</TableCell>
                        <TableCell>{r.disposal_type}</TableCell>
                        <TableCell>{r.disposal_date}</TableCell>
                        <TableCell>{r.unit_cost}</TableCell>
                        <TableCell sx={{ color: 'error.main' }}>{r._error}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {bulkRows.length > 100 && (
                <Typography variant="caption" color="text.secondary">Showing first 100 of {bulkRows.length} rows.</Typography>
              )}
            </>
          )}

          {bulkResult && (
            <>
              <Alert severity={bulkResult.failed ? 'warning' : 'success'} sx={{ mb: 1 }}>
                Recorded {bulkResult.created} disposal(s){bulkResult.failed ? `; ${bulkResult.failed} failed` : ''}.
              </Alert>
              {bulkResult.failed > 0 && Array.isArray(bulkResult.results) && (
                <TableContainer component={Paper} sx={{ maxHeight: 260 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow><TableCell>Row</TableCell><TableCell>SKU</TableCell><TableCell>Error</TableCell></TableRow>
                    </TableHead>
                    <TableBody>
                      {bulkResult.results.filter((x) => x.error).map((x, i) => (
                        <TableRow key={i}>
                          <TableCell>{x.row}</TableCell>
                          <TableCell>{x.sku}</TableCell>
                          <TableCell sx={{ color: 'error.main' }}>{x.error}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBulkUpload}>{bulkResult ? 'Close' : 'Cancel'}</Button>
          {!bulkResult && (
            <Button variant="contained" onClick={submitBulkUpload} disabled={bulkSubmitting || bulkValidCount === 0}>
              {bulkSubmitting ? 'Uploading…' : `Upload ${bulkValidCount} disposal${bulkValidCount === 1 ? '' : 's'}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Disposals;
